import React, { useState, useEffect, useCallback } from "react";
import {
  CalendarCheck, MessageSquare, UtensilsCrossed, Images, LogOut,
  Check, X, Trash2, Plus, Upload, Link as LinkIcon, Phone, Users, Clock, Pencil, Mail, Power, Palette, LayoutGrid, Image as ImageIcon, PartyPopper,
  Settings as SettingsIcon, KeyRound, Save,
} from "lucide-react";
import { adminApi, setToken } from "./adminApi";
import { useToast } from "../hooks/use-toast";
import { resizeImage } from "./imageUtils";

const TABS = [
  { id: "reservations", label: "Prenotazioni", icon: CalendarCheck },
  { id: "messages", label: "Messaggi", icon: MessageSquare },
  { id: "menu", label: "Menù", icon: UtensilsCrossed },
  { id: "services", label: "Servizi", icon: LayoutGrid },
  { id: "catering", label: "Catering", icon: PartyPopper },
  { id: "about", label: "Chi Siamo", icon: ImageIcon },
  { id: "hours", label: "Orari", icon: Clock },
  { id: "gallery", label: "Galleria", icon: Images },
  { id: "backgrounds", label: "Sfondi", icon: Palette },
  { id: "settings", label: "Impostazioni", icon: SettingsIcon },
];

export default function Dashboard({ username, onLogout }) {
  const [tab, setTab] = useState("reservations");

  const logout = () => { setToken(null); onLogout(); };

  return (
    <div className="min-h-screen bg-[#f3ecdf]">
      <header className="bg-[#1C1815] text-white">
        <div className="max-w-6xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div>
            <p className="font-display text-xl">L'angolo <span className="italic text-[#C9A24B]">del Gusto</span></p>
            <p className="text-[10px] tracking-[0.3em] uppercase text-white/40">Gestionale</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/60 hidden sm:block">Ciao, {username}</span>
            <a href="/" className="text-sm text-[#C9A24B] hover:underline">Vedi sito</a>
            <button onClick={logout} className="flex items-center gap-1.5 text-sm border border-white/20 hover:bg-white/10 px-4 py-2 rounded-full transition-colors">
              <LogOut size={15} /> Esci
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 md:px-8 py-8">
        <div className="flex flex-wrap gap-2 mb-8">
          {TABS.map((tb) => (
            <button key={tb.id} onClick={() => setTab(tb.id)}
              className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                tab === tb.id ? "bg-[#1C1815] text-[#C9A24B]" : "bg-white text-[#5a5347] hover:bg-[#efe6d4] border border-[#e5dcc8]"
              }`}>
              <tb.icon size={16} /> {tb.label}
            </button>
          ))}
        </div>

        {tab === "reservations" && <Reservations />}
        {tab === "messages" && <Messages />}
        {tab === "menu" && <MenuManager />}
        {tab === "services" && <ServicesManager />}
        {tab === "catering" && <CateringManager />}
        {tab === "about" && <AboutImagesManager />}
        {tab === "hours" && <HoursManager />}
        {tab === "gallery" && <GalleryManager />}
        {tab === "backgrounds" && <BackgroundsManager />}
        {tab === "settings" && <SettingsPanel />}
      </div>
    </div>
  );
}

function Card({ children, className = "" }) {
  return <div className={`bg-white rounded-[2px] border border-[#e5dcc8] shadow-sm ${className}`}>{children}</div>;
}

function Empty({ text }) {
  return <Card className="p-10 text-center text-[#8a7f6b]">{text}</Card>;
}

const statusStyle = {
  pending: "bg-amber-100 text-amber-800",
  confirmed: "bg-green-100 text-green-800",
  cancelled: "bg-red-100 text-red-700",
};
const statusLabel = { pending: "In attesa", confirmed: "Confermata", cancelled: "Annullata" };

function Reservations() {
  const { toast } = useToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const r = await adminApi.reservations(); setList(r.data); } catch (e) {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await adminApi.setReservationStatus(id, status);
    toast({ title: "Prenotazione aggiornata" });
    load();
  };
  const remove = async (id) => { await adminApi.deleteReservation(id); toast({ title: "Prenotazione eliminata" }); load(); };

  if (loading) return <Empty text="Caricamento..." />;
  if (!list.length) return <Empty text="Nessuna prenotazione ricevuta." />;

  return (
    <div className="grid gap-4">
      {list.map((r) => (
        <Card key={r.id} className="p-5">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-display text-xl text-[#23201C]">{r.name}</h3>
                <span className={`text-[11px] uppercase tracking-wider px-2.5 py-0.5 rounded-full ${statusStyle[r.status]}`}>{statusLabel[r.status]}</span>
              </div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-[#5a5347]">
                <span className="flex items-center gap-1.5"><CalendarCheck size={14} className="text-[#C9A24B]" />{r.date} · {r.time}</span>
                <span className="flex items-center gap-1.5"><Users size={14} className="text-[#C9A24B]" />{r.guests} persone</span>
                <a href={`tel:${r.phone}`} className="flex items-center gap-1.5 hover:text-[#B0863B]"><Phone size={14} className="text-[#C9A24B]" />{r.phone}</a>
                {r.email ? <a href={`mailto:${r.email}`} className="flex items-center gap-1.5 hover:text-[#B0863B]"><Mail size={14} className="text-[#C9A24B]" />{r.email}</a> : null}
              </div>
              {r.notes ? <p className="text-sm text-[#8a7f6b] mt-2 italic">“{r.notes}”</p> : null}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setStatus(r.id, "confirmed")} title="Conferma" className="w-9 h-9 rounded-full bg-green-50 text-green-700 hover:bg-green-100 flex items-center justify-center"><Check size={16} /></button>
              <button onClick={() => setStatus(r.id, "cancelled")} title="Annulla" className="w-9 h-9 rounded-full bg-amber-50 text-amber-700 hover:bg-amber-100 flex items-center justify-center"><X size={16} /></button>
              <button onClick={() => remove(r.id)} title="Elimina" className="w-9 h-9 rounded-full bg-red-50 text-red-700 hover:bg-red-100 flex items-center justify-center"><Trash2 size={16} /></button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function Messages() {
  const { toast } = useToast();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try { const r = await adminApi.contacts(); setList(r.data); } catch (e) {} finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (id) => { await adminApi.deleteContact(id); toast({ title: "Messaggio eliminato" }); load(); };

  if (loading) return <Empty text="Caricamento..." />;
  if (!list.length) return <Empty text="Nessun messaggio ricevuto." />;

  return (
    <div className="grid gap-4">
      {list.map((m) => (
        <Card key={m.id} className="p-5">
          <div className="flex justify-between items-start gap-4">
            <div>
              <h3 className="font-display text-lg text-[#23201C]">{m.name}</h3>
              <a href={`mailto:${m.email}`} className="text-sm text-[#B0863B] hover:underline">{m.email}</a>
              <p className="text-[#5a5347] mt-2">{m.message}</p>
            </div>
            <button onClick={() => remove(m.id)} className="w-9 h-9 flex-shrink-0 rounded-full bg-red-50 text-red-700 hover:bg-red-100 flex items-center justify-center"><Trash2 size={16} /></button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function MenuManager() {
  const { toast } = useToast();
  const [cats, setCats] = useState([]);
  const emptyForm = { category_id: "", name: "", desc: "", name_en: "", desc_en: "", price: "", tag: "" };
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);

  const load = useCallback(async () => {
    const r = await adminApi.menu();
    setCats(r.data);
    if (r.data.length && !form.category_id) setForm((f) => ({ ...f, category_id: r.data[0].id }));
  }, [form.category_id]);
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startEdit = (catId, it) => {
    setEditingId(it.id);
    setForm({
      category_id: catId, name: it.name || "", desc: it.desc || "",
      name_en: it.name_en || "", desc_en: it.desc_en || "", price: it.price || "", tag: it.tag || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => { setEditingId(null); setForm((f) => ({ ...emptyForm, category_id: f.category_id })); };

  // ----- Category management -----
  const [newCat, setNewCat] = useState({ label: "", label_en: "" });
  const [editCatId, setEditCatId] = useState(null);
  const [editCatVal, setEditCatVal] = useState({ label: "", label_en: "" });

  const addCategory = async (e) => {
    e.preventDefault();
    if (!newCat.label.trim()) { toast({ title: "Inserisci il nome della categoria" }); return; }
    await adminApi.addCategory({ label: newCat.label, label_en: newCat.label_en || null });
    toast({ title: "Categoria aggiunta" });
    setNewCat({ label: "", label_en: "" });
    load();
  };
  const startEditCat = (c) => { setEditCatId(c.id); setEditCatVal({ label: c.label, label_en: c.label_en || "" }); };
  const saveCat = async (id) => {
    await adminApi.updateCategory(id, { label: editCatVal.label, label_en: editCatVal.label_en });
    toast({ title: "Categoria aggiornata" });
    setEditCatId(null); load();
  };
  const deleteCat = async (c) => {
    if (!window.confirm(`Eliminare la categoria "${c.label}" e tutti i suoi piatti?`)) return;
    await adminApi.deleteCategory(c.id);
    toast({ title: "Categoria eliminata" }); load();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.price || !form.category_id) { toast({ title: "Compila nome, prezzo e categoria" }); return; }
    const payload = { ...form, tag: form.tag || null, name_en: form.name_en || null, desc_en: form.desc_en || null };
    if (editingId) {
      await adminApi.updateMenuItem(editingId, payload);
      toast({ title: "Piatto modificato" });
      setEditingId(null);
    } else {
      await adminApi.addMenuItem(payload);
      toast({ title: "Piatto aggiunto" });
    }
    setForm((f) => ({ ...emptyForm, category_id: f.category_id }));
    load();
  };
  const remove = async (id) => {
    await adminApi.deleteMenuItem(id);
    if (editingId === id) cancelEdit();
    toast({ title: "Piatto eliminato" }); load();
  };

  const inputCls = "w-full bg-white border border-[#e0d6c2] rounded-[2px] px-3 py-2.5 text-[#23201C] text-sm outline-none focus:border-[#C9A24B] focus:ring-2 focus:ring-[#C9A24B]/20 transition";

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className={`p-6 h-fit lg:sticky lg:top-6 ${editingId ? "ring-2 ring-[#C9A24B]" : ""}`}>
        <h3 className="font-display text-xl text-[#23201C] mb-4">{editingId ? "Modifica piatto" : "Aggiungi piatto"}</h3>
        <form onSubmit={submit} className="space-y-3">
          <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className={inputCls}>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <p className="text-[11px] uppercase tracking-wider text-[#B0863B] font-medium pt-1">Italiano 🇮🇹</p>
          <input placeholder="Nome piatto" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          <textarea placeholder="Descrizione" rows={2} value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} className={`${inputCls} resize-none`} />
          <p className="text-[11px] uppercase tracking-wider text-[#B0863B] font-medium pt-1">English 🇬🇧 (opzionale)</p>
          <input placeholder="Dish name (EN)" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className={inputCls} />
          <textarea placeholder="Description (EN)" rows={2} value={form.desc_en} onChange={(e) => setForm({ ...form, desc_en: e.target.value })} className={`${inputCls} resize-none`} />
          <div className="flex gap-3">
            <input placeholder="Prezzo (es. 9,50)" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className={inputCls} />
            <input placeholder="Tag (opz.)" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} className={inputCls} />
          </div>
          <div className="flex gap-2">
            <button type="submit" className="flex-1 flex items-center justify-center gap-2 bg-[#1C1815] text-[#C9A24B] px-4 py-2.5 rounded-full text-sm font-medium hover:bg-[#2a241f] transition-colors">
              {editingId ? <><Save size={15} /> Salva</> : <><Plus size={15} /> Aggiungi</>}
            </button>
            {editingId && (
              <button type="button" onClick={cancelEdit} className="px-4 py-2.5 rounded-full text-sm font-medium border border-[#d8cbb0] text-[#5a5347] hover:bg-[#efe6d4] transition-colors">
                Annulla
              </button>
            )}
          </div>
        </form>
      </Card>

      <div className="lg:col-span-2 space-y-6">
        <Card className="p-6">
          <h3 className="font-display text-lg text-[#23201C] mb-3">Gestione categorie</h3>
          <form onSubmit={addCategory} className="flex flex-col sm:flex-row gap-2 mb-4">
            <input placeholder="Nuova categoria (IT)" value={newCat.label} onChange={(e) => setNewCat({ ...newCat, label: e.target.value })} className={inputCls} />
            <input placeholder="Category (EN, opz.)" value={newCat.label_en} onChange={(e) => setNewCat({ ...newCat, label_en: e.target.value })} className={inputCls} />
            <button type="submit" className="flex-shrink-0 flex items-center justify-center gap-2 bg-[#1C1815] text-[#C9A24B] px-4 py-2.5 rounded-full text-sm font-medium hover:bg-[#2a241f] transition-colors"><Plus size={15} /> Aggiungi</button>
          </form>
          <div className="flex flex-wrap gap-2">
            {cats.map((c) => (
              <div key={c.id} className="flex items-center gap-1.5 bg-[#f3ecdf] rounded-full pl-3 pr-1.5 py-1 text-sm">
                {editCatId === c.id ? (
                  <>
                    <input value={editCatVal.label} onChange={(e) => setEditCatVal({ ...editCatVal, label: e.target.value })} className="w-24 bg-white border border-[#e0d6c2] rounded px-2 py-1 text-sm outline-none" />
                    <input value={editCatVal.label_en} onChange={(e) => setEditCatVal({ ...editCatVal, label_en: e.target.value })} placeholder="EN" className="w-20 bg-white border border-[#e0d6c2] rounded px-2 py-1 text-sm outline-none" />
                    <button onClick={() => saveCat(c.id)} className="w-6 h-6 rounded-full bg-green-100 text-green-700 flex items-center justify-center"><Check size={13} /></button>
                    <button onClick={() => setEditCatId(null)} className="w-6 h-6 rounded-full bg-[#e9dcc2] text-[#8a7f6b] flex items-center justify-center"><X size={13} /></button>
                  </>
                ) : (
                  <>
                    <span className="text-[#5a5347] font-medium">{c.label}</span>
                    <button onClick={() => startEditCat(c)} title="Rinomina" className="w-6 h-6 rounded-full text-[#B0863B] hover:bg-[#e9dcc2] flex items-center justify-center"><Pencil size={12} /></button>
                    <button onClick={() => deleteCat(c)} title="Elimina" className="w-6 h-6 rounded-full text-red-600 hover:bg-red-100 flex items-center justify-center"><Trash2 size={12} /></button>
                  </>
                )}
              </div>
            ))}
          </div>
        </Card>

        {cats.map((c) => (
          <Card key={c.id} className="p-6">
            <h3 className="font-display text-lg text-[#B0863B] mb-3">{c.label}</h3>
            <div className="divide-y divide-[#efe6d4]">
              {c.items.map((it) => (
                <div key={it.id} className={`py-3 flex items-start justify-between gap-4 ${editingId === it.id ? "bg-[#faf5ea] -mx-2 px-2 rounded" : ""}`}>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-[#23201C]">{it.name}</span>
                      {it.name_en && <span className="text-xs text-[#8a7f6b]">/ {it.name_en}</span>}
                      {it.tag && <span className="text-[10px] uppercase bg-[#C9A24B]/15 text-[#B0863B] px-2 py-0.5 rounded-full">{it.tag}</span>}
                      <span className="text-[#C9A24B] font-display">€ {it.price}</span>
                    </div>
                    <p className="text-sm text-[#8a7f6b]">{it.desc}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => startEdit(c.id, it)} title="Modifica" className="w-8 h-8 rounded-full bg-[#f3ecdf] text-[#B0863B] hover:bg-[#e9dcc2] flex items-center justify-center"><Pencil size={14} /></button>
                    <button onClick={() => remove(it.id)} title="Elimina" className="w-8 h-8 rounded-full bg-red-50 text-red-700 hover:bg-red-100 flex items-center justify-center"><Trash2 size={15} /></button>
                  </div>
                </div>
              ))}
              {!c.items.length && <p className="py-3 text-sm text-[#8a7f6b]">Nessun piatto in questa categoria.</p>}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

const DAY_LABELS = { mon: "Lunedì", tue: "Martedì", wed: "Mercoledì", thu: "Giovedì", fri: "Venerdì", sat: "Sabato", sun: "Domenica" };

function HoursManager() {
  const { toast } = useToast();
  const [hours, setHours] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => { const r = await adminApi.hours(); setHours(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const change = (i, val) => setHours((h) => h.map((x, idx) => (idx === i ? { ...x, time: val } : x)));
  const toggleClosed = (i) => setHours((h) => h.map((x, idx) => (idx === i ? { ...x, closed: !x.closed } : x)));

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.updateHours(hours.map((h, i) => ({ dayKey: h.dayKey, time: h.time, closed: !!h.closed, order: i + 1 })));
      toast({ title: "Orari aggiornati", description: "Le modifiche sono già online sul sito." });
    } catch (err) { toast({ title: "Errore", description: "Riprova." }); }
    finally { setSaving(false); }
  };

  const inputCls = "w-full bg-white border border-[#e0d6c2] rounded-[2px] px-3 py-2.5 text-[#23201C] text-sm outline-none focus:border-[#C9A24B] focus:ring-2 focus:ring-[#C9A24B]/20 transition disabled:bg-[#f3ecdf] disabled:text-[#b7ab95]";

  return (
    <div className="max-w-2xl">
      <Card className="p-8">
        <div className="flex items-center gap-2 mb-2">
          <Clock size={20} className="text-[#C9A24B]" />
          <h3 className="font-display text-xl text-[#23201C]">Orari di apertura</h3>
        </div>
        <p className="text-sm text-[#8a7f6b] mb-6">Modifica gli orari per ogni giorno. Usa l'interruttore "Chiuso" per i giorni di riposo.</p>
        <div className="space-y-3">
          {hours.map((h, i) => (
            <div key={h.dayKey} className="flex items-center gap-3">
              <span className="w-24 text-sm font-medium text-[#5a5347] flex-shrink-0">{DAY_LABELS[h.dayKey] || h.dayKey}</span>
              <input value={h.closed ? "" : h.time} disabled={h.closed} placeholder={h.closed ? "Chiuso" : ""} onChange={(e) => change(i, e.target.value)} className={inputCls} />
              <button type="button" onClick={() => toggleClosed(i)}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium transition-colors ${
                  h.closed ? "bg-[#b0553c] text-white" : "bg-[#efe6d4] text-[#8a7f6b] hover:bg-[#e5d8bf]"
                }`}>
                <Power size={13} /> {h.closed ? "Chiuso" : "Aperto"}
              </button>
            </div>
          ))}
        </div>
        <button onClick={save} disabled={saving} className="mt-6 w-full flex items-center justify-center gap-2 bg-[#1C1815] text-[#C9A24B] px-4 py-3 rounded-full text-sm font-medium hover:bg-[#2a241f] transition-colors disabled:opacity-60">
          <Save size={16} /> {saving ? "Salvataggio..." : "Salva orari"}
        </button>
      </Card>
    </div>
  );
}

const CATERING_ICONS = ["heart", "briefcase", "party", "utensils", "chef", "users", "star", "calendar", "wine", "gift"];

function CateringManager() {
  const { toast } = useToast();
  const [info, setInfo] = useState(null);
  const [items, setItems] = useState([]);
  const emptyItem = { icon: "heart", title: "", title_en: "", desc: "", desc_en: "" };
  const [itemForm, setItemForm] = useState(emptyItem);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const r = await adminApi.catering();
    setInfo(r.data.info); setItems(r.data.items);
  }, []);
  useEffect(() => { load(); }, [load]);

  const setField = (k, v) => setInfo((s) => ({ ...s, [k]: v }));
  const saveInfo = async () => {
    await adminApi.updateCateringInfo(info);
    toast({ title: "Sezione salvata" }); load();
  };
  const uploadImg = async (field, file) => {
    if (!file) return;
    setBusy(field);
    try { const resized = await resizeImage(file); const r = await adminApi.uploadImage(resized); setField(field, r.data.url); toast({ title: "Immagine caricata (ricordati di Salvare)" }); }
    catch (err) { toast({ title: "Errore", description: err?.response?.data?.detail || "" }); }
    finally { setBusy(""); }
  };

  const submitItem = async (e) => {
    e.preventDefault();
    if (!itemForm.title) { toast({ title: "Inserisci il titolo" }); return; }
    try {
      if (editingId) { await adminApi.updateCateringItem(editingId, itemForm); toast({ title: "Voce modificata" }); setEditingId(null); }
      else { await adminApi.addCateringItem(itemForm); toast({ title: "Voce aggiunta" }); }
      setItemForm(emptyItem); load();
    } catch (err) { toast({ title: "Errore nel salvataggio" }); }
  };
  const editItem = (it) => { setEditingId(it.id); setItemForm({ icon: it.icon || "heart", title: it.title || "", title_en: it.title_en || "", desc: it.desc || "", desc_en: it.desc_en || "" }); };
  const removeItem = async (id) => { await adminApi.deleteCateringItem(id); if (editingId === id) { setEditingId(null); setItemForm(emptyItem); } toast({ title: "Voce eliminata" }); load(); };

  const inputCls = "w-full bg-white border border-[#e0d6c2] rounded-[2px] px-3 py-2.5 text-[#23201C] text-sm outline-none focus:border-[#C9A24B] focus:ring-2 focus:ring-[#C9A24B]/20 transition";
  if (!info) return <Empty text="Caricamento..." />;

  const ImgField = (field, label) => (
    <div>
      <p className="text-xs text-[#8a7f6b] mb-1">{label}</p>
      <div className="h-28 rounded-[2px] border border-[#e5dcc8] overflow-hidden bg-[#f3ecdf] mb-2">
        {info[field] ? <img src={info[field]} alt={label} className="w-full h-full object-cover" /> : null}
      </div>
      <div className="flex gap-2">
        <label className={`flex-1 flex items-center justify-center gap-1.5 border border-dashed border-[#d8cbb0] rounded-[2px] py-2 cursor-pointer hover:border-[#C9A24B] text-xs text-[#5a5347] ${busy === field ? "opacity-60" : ""}`}>
          <Upload size={13} className="text-[#C9A24B]" /> {busy === field ? "..." : "Carica"}
          <input type="file" accept="image/*" className="hidden" disabled={busy === field} onChange={(e) => uploadImg(field, e.target.files?.[0])} />
        </label>
        <input placeholder="o link" value={info[field] || ""} onChange={(e) => setField(field, e.target.value)} className={inputCls} />
      </div>
    </div>
  );

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Card className="p-6 h-fit">
        <h3 className="font-display text-xl text-[#23201C] mb-4">Testi della sezione</h3>
        <div className="space-y-3">
          <input placeholder="Etichetta (IT)" value={info.badge || ""} onChange={(e) => setField("badge", e.target.value)} className={inputCls} />
          <input placeholder="Label (EN)" value={info.badge_en || ""} onChange={(e) => setField("badge_en", e.target.value)} className={inputCls} />
          <div className="flex gap-2">
            <input placeholder="Titolo (IT)" value={info.title || ""} onChange={(e) => setField("title", e.target.value)} className={inputCls} />
            <input placeholder="Evidenziato (IT)" value={info.highlight || ""} onChange={(e) => setField("highlight", e.target.value)} className={inputCls} />
          </div>
          <div className="flex gap-2">
            <input placeholder="Title (EN)" value={info.title_en || ""} onChange={(e) => setField("title_en", e.target.value)} className={inputCls} />
            <input placeholder="Highlight (EN)" value={info.highlight_en || ""} onChange={(e) => setField("highlight_en", e.target.value)} className={inputCls} />
          </div>
          <textarea placeholder="Sottotitolo (IT)" rows={3} value={info.subtitle || ""} onChange={(e) => setField("subtitle", e.target.value)} className={`${inputCls} resize-none`} />
          <textarea placeholder="Subtitle (EN)" rows={3} value={info.subtitle_en || ""} onChange={(e) => setField("subtitle_en", e.target.value)} className={`${inputCls} resize-none`} />
          <div className="flex gap-2">
            <input placeholder="Testo pulsante (IT)" value={info.cta || ""} onChange={(e) => setField("cta", e.target.value)} className={inputCls} />
            <input placeholder="Button (EN)" value={info.cta_en || ""} onChange={(e) => setField("cta_en", e.target.value)} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-3 pt-2">
            {ImgField("image1", "Foto grande")}
            {ImgField("image2", "Foto piccola")}
          </div>
          <button onClick={saveInfo} className="w-full flex items-center justify-center gap-2 bg-[#1C1815] text-[#C9A24B] px-4 py-3 rounded-full text-sm font-medium hover:bg-[#2a241f] transition-colors">
            <Save size={16} /> Salva testi e foto
          </button>
        </div>
      </Card>

      <Card className="p-6 h-fit">
        <h3 className="font-display text-xl text-[#23201C] mb-4">Tipi di evento</h3>
        <form onSubmit={submitItem} className="space-y-3 mb-5 pb-5 border-b border-[#efe6d4]">
          <select value={itemForm.icon} onChange={(e) => setItemForm({ ...itemForm, icon: e.target.value })} className={inputCls}>
            {CATERING_ICONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
          </select>
          <div className="flex gap-2">
            <input placeholder="Titolo (IT)" value={itemForm.title} onChange={(e) => setItemForm({ ...itemForm, title: e.target.value })} className={inputCls} />
            <input placeholder="Title (EN)" value={itemForm.title_en} onChange={(e) => setItemForm({ ...itemForm, title_en: e.target.value })} className={inputCls} />
          </div>
          <input placeholder="Descrizione (IT)" value={itemForm.desc} onChange={(e) => setItemForm({ ...itemForm, desc: e.target.value })} className={inputCls} />
          <input placeholder="Description (EN)" value={itemForm.desc_en} onChange={(e) => setItemForm({ ...itemForm, desc_en: e.target.value })} className={inputCls} />
          <div className="flex gap-2">
            <button type="submit" className="flex-1 flex items-center justify-center gap-2 bg-[#1C1815] text-[#C9A24B] px-4 py-2.5 rounded-full text-sm font-medium hover:bg-[#2a241f] transition-colors">
              {editingId ? <><Save size={15} /> Salva voce</> : <><Plus size={15} /> Aggiungi voce</>}
            </button>
            {editingId && <button type="button" onClick={() => { setEditingId(null); setItemForm(emptyItem); }} className="px-4 py-2.5 rounded-full text-sm border border-[#d8cbb0] text-[#5a5347]">Annulla</button>}
          </div>
        </form>
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className={`flex items-center justify-between gap-3 py-2 px-3 rounded-[2px] ${editingId === it.id ? "bg-[#faf5ea]" : ""}`}>
              <div>
                <span className="font-medium text-[#23201C] text-sm">{it.title}</span>
                <span className="text-xs text-[#8a7f6b] ml-1">({it.icon})</span>
                <p className="text-xs text-[#8a7f6b]">{it.desc}</p>
              </div>
              <div className="flex gap-1.5 flex-shrink-0">
                <button onClick={() => editItem(it)} className="w-8 h-8 rounded-full bg-[#f3ecdf] text-[#B0863B] hover:bg-[#e9dcc2] flex items-center justify-center"><Pencil size={13} /></button>
                <button onClick={() => removeItem(it.id)} className="w-8 h-8 rounded-full bg-red-50 text-red-700 hover:bg-red-100 flex items-center justify-center"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}


function AboutImagesManager() {
  const { toast } = useToast();
  const [imgs, setImgs] = useState({ main: "", accent: "" });
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => { const r = await adminApi.aboutImages(); setImgs(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const saveField = async (field, value) => {
    await adminApi.updateAboutImages({ [field]: value });
    toast({ title: "Immagine aggiornata" });
    load();
  };
  const onUpload = async (field, file) => {
    if (!file) return;
    setBusy(field);
    try {
      const resized = await resizeImage(file);
      const r = await adminApi.uploadImage(resized);
      await saveField(field, r.data.url);
    } catch (err) { toast({ title: "Errore", description: err?.response?.data?.detail || "" }); }
    finally { setBusy(""); }
  };

  const inputCls = "w-full bg-white border border-[#e0d6c2] rounded-[2px] px-3 py-2.5 text-[#23201C] text-sm outline-none focus:border-[#C9A24B] focus:ring-2 focus:ring-[#C9A24B]/20 transition";

  const Field = ({ field, label, hint }) => (
    <Card className="p-5">
      <h3 className="font-display text-lg text-[#23201C] mb-1">{label}</h3>
      <p className="text-xs text-[#8a7f6b] mb-3">{hint}</p>
      <div className="h-48 rounded-[2px] border border-[#e5dcc8] overflow-hidden bg-[#f3ecdf] mb-4">
        {imgs[field] ? <img src={imgs[field]} alt={label} className="w-full h-full object-cover" /> : null}
      </div>
      <label className={`flex items-center justify-center gap-2 border border-dashed border-[#d8cbb0] rounded-[2px] py-2.5 cursor-pointer hover:border-[#C9A24B] transition-colors text-sm text-[#5a5347] mb-3 ${busy === field ? "opacity-60" : ""}`}>
        <Upload size={15} className="text-[#C9A24B]" /> {busy === field ? "Carico..." : "Carica dal dispositivo"}
        <input type="file" accept="image/*" className="hidden" disabled={busy === field} onChange={(e) => onUpload(field, e.target.files?.[0])} />
      </label>
      <div className="flex gap-2">
        <input placeholder="Oppure incolla un link immagine" defaultValue="" className={inputCls}
          onKeyDown={(e) => { if (e.key === "Enter") { saveField(field, e.target.value); e.target.value = ""; } }}
          id={`about-${field}`} />
        <button onClick={() => { const el = document.getElementById(`about-${field}`); if (el && el.value) { saveField(field, el.value); el.value = ""; } }}
          className="flex-shrink-0 bg-[#1C1815] text-[#C9A24B] px-3 rounded-[2px] hover:bg-[#2a241f] transition-colors"><LinkIcon size={15} /></button>
      </div>
    </Card>
  );

  return (
    <div>
      <p className="text-sm text-[#8a7f6b] mb-6 max-w-2xl">Cambia le due foto della sezione "Chi Siamo": l'immagine grande principale e la piccola immagine in primo piano.</p>
      <div className="grid md:grid-cols-2 gap-5 max-w-3xl">
        <Field field="main" label="Foto principale (grande)" hint="L'immagine grande sulla sinistra" />
        <Field field="accent" label="Foto in primo piano (piccola)" hint="La piccola immagine sovrapposta" />
      </div>
    </div>
  );
}


function ServicesManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const empty = { title: "", title_en: "", desc: "", desc_en: "", image: "", link: "", cta: "Scopri", cta_en: "Discover" };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => { const r = await adminApi.services(); setItems(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const startEdit = (s) => {
    setEditingId(s.id);
    setForm({ title: s.title || "", title_en: s.title_en || "", desc: s.desc || "", desc_en: s.desc_en || "", image: s.image || "", link: s.link || "", cta: s.cta || "Scopri", cta_en: s.cta_en || "Discover" });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancel = () => { setEditingId(null); setForm(empty); };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const resized = await resizeImage(file);
      const r = await adminApi.uploadImage(resized);
      setForm((f) => ({ ...f, image: r.data.url }));
      toast({ title: "Immagine caricata" });
    }
    catch (err) { toast({ title: "Errore nel caricamento", description: err?.response?.data?.detail || "Immagine troppo grande o formato non valido" }); }
    finally { setUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.desc) { toast({ title: "Compila titolo e descrizione" }); return; }
    try {
      if (editingId) { await adminApi.updateService(editingId, form); toast({ title: "Servizio modificato" }); setEditingId(null); }
      else { await adminApi.addService(form); toast({ title: "Servizio aggiunto" }); }
      setForm(empty); load();
    } catch (err) {
      toast({ title: "Errore nel salvataggio", description: err?.response?.data?.detail || "Riprova (immagine troppo grande?)" });
    }
  };
  const remove = async (id) => { await adminApi.deleteService(id); if (editingId === id) cancel(); toast({ title: "Servizio eliminato" }); load(); };

  const inputCls = "w-full bg-white border border-[#e0d6c2] rounded-[2px] px-3 py-2.5 text-[#23201C] text-sm outline-none focus:border-[#C9A24B] focus:ring-2 focus:ring-[#C9A24B]/20 transition";

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <Card className={`p-6 h-fit lg:sticky lg:top-6 ${editingId ? "ring-2 ring-[#C9A24B]" : ""}`}>
        <h3 className="font-display text-xl text-[#23201C] mb-4">{editingId ? "Modifica servizio" : "Aggiungi servizio"}</h3>
        <form onSubmit={submit} className="space-y-3">
          <div className="h-32 rounded-[2px] border border-[#e5dcc8] overflow-hidden bg-[#f3ecdf] flex items-center justify-center">
            {form.image ? <img src={form.image} alt="preview" className="w-full h-full object-cover" /> : <span className="text-xs text-[#b7ab95]">Nessuna immagine</span>}
          </div>
          <div className="flex gap-2">
            <label className={`flex-1 flex items-center justify-center gap-1.5 border border-dashed border-[#d8cbb0] rounded-[2px] py-2 cursor-pointer hover:border-[#C9A24B] transition-colors text-xs text-[#5a5347] ${uploading ? "opacity-60" : ""}`}>
              <Upload size={14} className="text-[#C9A24B]" /> {uploading ? "Carico..." : "Carica"}
              <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => onUpload(e.target.files?.[0])} />
            </label>
          </div>
          <input placeholder="Link immagine (in alternativa)" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className={inputCls} />
          <p className="text-[11px] uppercase tracking-wider text-[#B0863B] font-medium pt-1">Italiano 🇮🇹</p>
          <input placeholder="Titolo" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className={inputCls} />
          <textarea placeholder="Descrizione" rows={2} value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} className={`${inputCls} resize-none`} />
          <input placeholder="Testo pulsante (es. Scopri)" value={form.cta} onChange={(e) => setForm({ ...form, cta: e.target.value })} className={inputCls} />
          <p className="text-[11px] uppercase tracking-wider text-[#B0863B] font-medium pt-1">English 🇬🇧 (opzionale)</p>
          <input placeholder="Title (EN)" value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} className={inputCls} />
          <textarea placeholder="Description (EN)" rows={2} value={form.desc_en} onChange={(e) => setForm({ ...form, desc_en: e.target.value })} className={`${inputCls} resize-none`} />
          <input placeholder="Button text (EN)" value={form.cta_en} onChange={(e) => setForm({ ...form, cta_en: e.target.value })} className={inputCls} />
          <input placeholder="Link (es. #prenota o URL)" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} className={inputCls} />
          <div className="flex gap-2">
            <button type="submit" className="flex-1 flex items-center justify-center gap-2 bg-[#1C1815] text-[#C9A24B] px-4 py-2.5 rounded-full text-sm font-medium hover:bg-[#2a241f] transition-colors">
              {editingId ? <><Save size={15} /> Salva</> : <><Plus size={15} /> Aggiungi</>}
            </button>
            {editingId && <button type="button" onClick={cancel} className="px-4 py-2.5 rounded-full text-sm font-medium border border-[#d8cbb0] text-[#5a5347] hover:bg-[#efe6d4] transition-colors">Annulla</button>}
          </div>
        </form>
      </Card>

      <div className="lg:col-span-2 grid sm:grid-cols-2 gap-4 h-fit">
        {items.map((s) => (
          <Card key={s.id} className={`overflow-hidden ${editingId === s.id ? "ring-2 ring-[#C9A24B]" : ""}`}>
            <div className="h-36 bg-[#f3ecdf] overflow-hidden">
              {s.image ? <img src={s.image} alt={s.title} className="w-full h-full object-cover" /> : null}
            </div>
            <div className="p-4">
              <h4 className="font-display text-lg text-[#23201C]">{s.title}{s.title_en ? <span className="text-xs text-[#8a7f6b] font-sans"> / {s.title_en}</span> : null}</h4>
              <p className="text-sm text-[#8a7f6b] line-clamp-2 mt-1">{s.desc}</p>
              <div className="flex items-center gap-2 mt-3">
                <button onClick={() => startEdit(s)} className="flex items-center gap-1 text-xs text-[#B0863B] bg-[#f3ecdf] hover:bg-[#e9dcc2] px-3 py-1.5 rounded-full"><Pencil size={13} /> Modifica</button>
                <button onClick={() => remove(s.id)} className="flex items-center gap-1 text-xs text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full"><Trash2 size={13} /> Elimina</button>
              </div>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}


const SECTION_LABELS = [
  { key: "hero", label: "Hero (apertura)" },
  { key: "about", label: "Chi Siamo" },
  { key: "services", label: "Servizi" },
  { key: "menu", label: "Menù" },
  { key: "reservation", label: "Prenota" },
  { key: "gallery", label: "Galleria" },
  { key: "catering", label: "Catering" },
  { key: "testimonials", label: "Recensioni" },
  { key: "contact", label: "Contatti" },
];

function BackgroundsManager() {
  const { toast } = useToast();
  const [bgs, setBgs] = useState({});
  const [urlInputs, setUrlInputs] = useState({});
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const r = await adminApi.backgrounds();
    const m = {};
    r.data.forEach((b) => { m[b.section] = b; });
    setBgs(m);
  }, []);
  useEffect(() => { load(); }, [load]);

  const applyColor = async (section, color) => {
    await adminApi.updateBackground(section, { type: "color", value: color });
    toast({ title: "Sfondo aggiornato" });
    load();
  };
  const applyUrl = async (section) => {
    const url = urlInputs[section];
    if (!url) return;
    await adminApi.updateBackground(section, { type: "image", value: url });
    toast({ title: "Sfondo aggiornato" });
    setUrlInputs((u) => ({ ...u, [section]: "" }));
    load();
  };
  const uploadFile = async (section, file) => {
    if (!file) return;
    setBusy(section);
    try {
      const resized = await resizeImage(file, 2000, 0.82);
      await adminApi.uploadBackground(section, resized);
      toast({ title: "Sfondo caricato" });
      load();
    } catch (err) {
      toast({ title: "Errore", description: err?.response?.data?.detail || "" });
    } finally { setBusy(""); }
  };

  const inputCls = "w-full bg-white border border-[#e0d6c2] rounded-[2px] px-3 py-2 text-[#23201C] text-sm outline-none focus:border-[#C9A24B] focus:ring-2 focus:ring-[#C9A24B]/20 transition";

  return (
    <div>
      <p className="text-sm text-[#8a7f6b] mb-6 max-w-2xl">Personalizza lo sfondo di ogni sezione: scegli un colore oppure un'immagine (caricata o da link). Suggerimento: per le sezioni con testo chiaro (Hero, Servizi, Catering, Contatti) usa immagini o colori scuri; per le altre usa toni chiari.</p>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
        {SECTION_LABELS.map((s) => {
          const cfg = bgs[s.key] || { type: "color", value: "#FBF7F0" };
          return (
            <Card key={s.key} className="p-5">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-lg text-[#23201C]">{s.label}</h3>
                <span className="text-[10px] uppercase tracking-wider bg-[#f3ecdf] text-[#8a7f6b] px-2 py-0.5 rounded-full">{cfg.type === "image" ? "Immagine" : "Colore"}</span>
              </div>
              <div className="h-24 rounded-[2px] mb-4 border border-[#e5dcc8] overflow-hidden">
                {cfg.type === "image"
                  ? <img src={cfg.value} alt={s.label} className="w-full h-full object-cover" />
                  : <div className="w-full h-full" style={{ backgroundColor: cfg.value }} />}
              </div>
              <div className="flex items-center gap-2 mb-3">
                <input type="color" value={cfg.type === "color" ? cfg.value : "#FBF7F0"} onChange={(e) => applyColor(s.key, e.target.value)} className="w-10 h-9 rounded border border-[#e0d6c2] cursor-pointer bg-white" title="Scegli colore" />
                <span className="text-xs text-[#8a7f6b]">Scegli un colore</span>
              </div>
              <div className="flex gap-2 mb-2">
                <label className={`flex-1 flex items-center justify-center gap-1.5 border border-dashed border-[#d8cbb0] rounded-[2px] py-2 cursor-pointer hover:border-[#C9A24B] transition-colors text-xs text-[#5a5347] ${busy === s.key ? "opacity-60" : ""}`}>
                  <Upload size={14} className="text-[#C9A24B]" /> {busy === s.key ? "Carico..." : "Carica immagine"}
                  <input type="file" accept="image/*" className="hidden" disabled={busy === s.key} onChange={(e) => uploadFile(s.key, e.target.files?.[0])} />
                </label>
              </div>
              <div className="flex gap-2">
                <input placeholder="Link immagine" value={urlInputs[s.key] || ""} onChange={(e) => setUrlInputs((u) => ({ ...u, [s.key]: e.target.value }))} className={inputCls} />
                <button onClick={() => applyUrl(s.key)} className="flex-shrink-0 bg-[#1C1815] text-[#C9A24B] px-3 rounded-[2px] hover:bg-[#2a241f] transition-colors"><LinkIcon size={14} /></button>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}


function SettingsPanel() {
  const { toast } = useToast();
  const [form, setForm] = useState({ current_password: "", new_username: "", new_password: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const inputCls = "w-full bg-white border border-[#e0d6c2] rounded-[2px] px-3 py-2.5 text-[#23201C] text-sm outline-none focus:border-[#C9A24B] focus:ring-2 focus:ring-[#C9A24B]/20 transition";

  const save = async (e) => {
    e.preventDefault();
    if (!form.current_password) { toast({ title: "Inserisci la password attuale" }); return; }
    if (form.new_password && form.new_password !== form.confirm) { toast({ title: "Le password non coincidono" }); return; }
    if (!form.new_username && !form.new_password) { toast({ title: "Inserisci un nuovo username o una nuova password" }); return; }
    setSaving(true);
    try {
      const res = await adminApi.updateCredentials({
        current_password: form.current_password,
        new_username: form.new_username || null,
        new_password: form.new_password || null,
      });
      if (res.data.token) setToken(res.data.token);
      toast({ title: "Credenziali aggiornate", description: "Usa le nuove credenziali al prossimo accesso." });
      setForm({ current_password: "", new_username: "", new_password: "", confirm: "" });
    } catch (err) {
      toast({ title: "Errore", description: err?.response?.data?.detail || "Riprova." });
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-lg">
      <Card className="p-8">
        <div className="flex items-center gap-2 mb-6">
          <KeyRound size={20} className="text-[#C9A24B]" />
          <h3 className="font-display text-xl text-[#23201C]">Cambia credenziali di accesso</h3>
        </div>
        <form onSubmit={save} className="space-y-4">
          <div>
            <label className="block text-sm text-[#5a5347] mb-1.5 font-medium">Password attuale *</label>
            <input type="password" value={form.current_password} onChange={(e) => setForm({ ...form, current_password: e.target.value })} className={inputCls} placeholder="Password attuale" />
          </div>
          <div className="border-t border-[#efe6d4] pt-4 space-y-4">
            <div>
              <label className="block text-sm text-[#5a5347] mb-1.5 font-medium">Nuovo username (opzionale)</label>
              <input value={form.new_username} onChange={(e) => setForm({ ...form, new_username: e.target.value })} className={inputCls} placeholder="Lascia vuoto per non cambiare" />
            </div>
            <div>
              <label className="block text-sm text-[#5a5347] mb-1.5 font-medium">Nuova password (opzionale, min 6)</label>
              <input type="password" value={form.new_password} onChange={(e) => setForm({ ...form, new_password: e.target.value })} className={inputCls} placeholder="Lascia vuoto per non cambiare" />
            </div>
            <div>
              <label className="block text-sm text-[#5a5347] mb-1.5 font-medium">Conferma nuova password</label>
              <input type="password" value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className={inputCls} placeholder="Ripeti la nuova password" />
            </div>
          </div>
          <button type="submit" disabled={saving} className="w-full flex items-center justify-center gap-2 bg-[#1C1815] text-[#C9A24B] px-4 py-3 rounded-full text-sm font-medium hover:bg-[#2a241f] transition-colors disabled:opacity-60">
            <Save size={16} /> {saving ? "Salvataggio..." : "Salva modifiche"}
          </button>
        </form>
      </Card>
    </div>
  );
}

function GalleryManager() {
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [alt, setAlt] = useState("");
  const [url, setUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => { const r = await adminApi.gallery(); setItems(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { const resized = await resizeImage(file); await adminApi.uploadGallery(resized, alt); toast({ title: "Foto caricata" }); setAlt(""); load(); }
    catch (err) { toast({ title: "Errore nel caricamento", description: err?.response?.data?.detail || "" }); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const addUrl = async () => {
    if (!url) return;
    try { await adminApi.addGalleryUrl(url, alt); toast({ title: "Immagine aggiunta" }); setUrl(""); setAlt(""); load(); }
    catch (err) { toast({ title: "Errore" }); }
  };
  const remove = async (id) => { await adminApi.deleteGallery(id); toast({ title: "Foto eliminata" }); load(); };

  const inputCls = "w-full bg-white border border-[#e0d6c2] rounded-[2px] px-3 py-2.5 text-[#23201C] text-sm outline-none focus:border-[#C9A24B] focus:ring-2 focus:ring-[#C9A24B]/20 transition";

  return (
    <div>
      <Card className="p-6 mb-6">
        <h3 className="font-display text-xl text-[#23201C] mb-4">Aggiungi foto reali</h3>
        <input placeholder="Descrizione foto (opzionale)" value={alt} onChange={(e) => setAlt(e.target.value)} className={`${inputCls} mb-4`} />
        <div className="grid sm:grid-cols-2 gap-4">
          <label className={`flex items-center justify-center gap-2 border-2 border-dashed border-[#d8cbb0] rounded-[2px] py-4 cursor-pointer hover:border-[#C9A24B] transition-colors ${uploading ? "opacity-60" : ""}`}>
            <Upload size={17} className="text-[#C9A24B]" />
            <span className="text-sm text-[#5a5347]">{uploading ? "Caricamento..." : "Carica dal dispositivo"}</span>
            <input type="file" accept="image/*" className="hidden" onChange={onFile} disabled={uploading} />
          </label>
          <div className="flex gap-2">
            <input placeholder="Oppure incolla un link immagine" value={url} onChange={(e) => setUrl(e.target.value)} className={inputCls} />
            <button onClick={addUrl} className="flex items-center gap-1.5 bg-[#1C1815] text-[#C9A24B] px-4 rounded-[2px] text-sm hover:bg-[#2a241f] transition-colors"><LinkIcon size={15} /></button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {items.map((g) => (
          <div key={g.id} className="group relative rounded-[2px] overflow-hidden border border-[#e5dcc8]">
            <img src={g.url} alt={g.alt} className="w-full h-40 object-cover" />
            <button onClick={() => remove(g.id)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 text-red-600 hover:bg-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
